import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CreditCard, ShieldCheck, ChevronLeft, Loader2 } from "lucide-react";

/**
 * Mock checkout page — only used when Cashfree keys are not configured.
 * Allows the recruiter to "confirm" the upgrade end-to-end so the UI/UX is
 * complete and testable without a live gateway.
 */
export default function MockCheckout() {
    const { checkoutId } = useParams();
    const navigate = useNavigate();
    const [confirming, setConfirming] = useState(false);

    const confirm = async () => {
        setConfirming(true);
        try {
            await api.post("/subscription/confirm", { checkout_id: checkoutId });
            toast.success("Subscription activated (mock)");
            navigate("/billing", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setConfirming(false); }
    };

    return (
        <div className="min-h-screen bg-secondary/30 flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-white border border-border p-6 sm:p-8">
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-300 px-2 py-1 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Mock checkout · No keys configured
                </div>
                <h1 className="font-display font-black text-3xl mt-4">Confirm your upgrade</h1>
                <p className="text-foreground/70 text-sm mt-2">
                    Cashfree isn't configured yet, so we're simulating the checkout. Press confirm to flip your subscription to active.
                </p>
                <div className="mt-4 text-xs font-mono text-foreground/50">Order ID: {checkoutId}</div>
                <div className="flex items-center justify-between gap-3 mt-6">
                    <Link to="/billing" className="text-sm text-ikb inline-flex items-center gap-1 hover:underline" data-testid="mock-back">
                        <ChevronLeft className="w-3 h-3" /> Back to billing
                    </Link>
                    <Button onClick={confirm} disabled={confirming} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="mock-confirm">
                        {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Confirm payment</>}
                    </Button>
                </div>
            </div>
        </div>
    );
}
